// Every version of every section of one issue, newest first. Read-only: nothing is changed
// from here, and an earlier version is never removed.

import { useEffect, useState } from 'react';

import type { ApiClient } from '../api/client.js';
import type { IssueEntryKind, IssueHistoryEntry, TopicView } from '../api/contract.js';
import { dayAndTime } from '../format.js';
import { basisWord, positionWord } from '../plan/issue.js';
import type { IssueState } from '../plan/useIssue.js';
import type { PlanState } from '../plan/usePlan.js';

export type IssueHistoryPageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
  topic: TopicView;
  issueId: string;
  issue: IssueState;
};

const SECTION_NAMES: Readonly<Record<IssueEntryKind, string>> = {
  intention: 'What you want',
  rationale: 'Why it matters',
  next_step: 'Suggested next step',
  arrangements: 'Existing arrangements',
  involvement: 'Who is involved',
  question: 'What’s still open',
  checking: 'What needs checking',
};

export function IssueHistoryPage({
  state,
  client,
  planId,
  topic,
  issueId,
  issue,
}: IssueHistoryPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const [entries, setEntries] = useState<readonly IssueHistoryEntry[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Another issue's history never shows while this one loads, and a failed read starts clean.
  useEffect(() => {
    setEntries(null);
    setFailed(false);
  }, [planId, issueId]);

  useEffect(() => {
    let current = true;
    client.readIssueHistory(planId, issueId).then(
      (loaded) => {
        if (current) {
          setEntries(loaded);
          setFailed(false);
        }
      },
      () => {
        if (current) {
          setFailed(true);
        }
      },
    );
    return () => {
      current = false;
    };
  }, [client, planId, issueId, snapshot]);

  if (snapshot === null) {
    return null;
  }
  if (issue.view?.removed === true) {
    return (
      <section className="page issue">
        <h2 className="page__title">{issue.view.title}</h2>
        <p className="note">This issue was removed from the list.</p>
      </section>
    );
  }
  const title =
    snapshot.decisions.find((decision) => decision.id === issueId)?.label ?? '';
  const nameOf = (id: string | null): string | null =>
    id === null
      ? null
      : (snapshot.people.find((person) => person.id === id)?.name ?? null);

  const facts = (entry: IssueHistoryEntry): string[] => {
    const parts: string[] = [];
    const who = nameOf(entry.personId);
    if (who !== null) {
      parts.push(entry.kind === 'checking' ? `Could answer: ${who}` : who);
    }
    if (entry.kind === 'checking' && entry.basis !== null) {
      const word = basisWord(entry.basis);
      if (word !== null) {
        parts.push(word);
      }
    }
    if (entry.position !== null) {
      parts.push(positionWord(entry.position));
    }
    if (entry.viewSoFar !== null && entry.viewSoFar !== '') {
      parts.push(`View so far: ${entry.viewSoFar}`);
    }
    if (entry.knownSoFar !== null && entry.knownSoFar !== '') {
      parts.push(`Known so far: ${entry.knownSoFar}`);
    }
    if (entry.professionalReview) {
      parts.push('Needs professional review');
    }
    if (entry.settled) {
      parts.push('Settled');
    }
    if (entry.dismissed) {
      parts.push('Dismissed');
    }
    return parts;
  };

  return (
    <section className="page issue">
      <h2 className="page__title">History</h2>
      {failed && entries === null ? (
        <p className="screen__problem">That didn’t load. Try again.</p>
      ) : entries === null ? null : entries.length === 0 ? (
        <p className="decisionNotes__empty">Nothing has been written about this yet.</p>
      ) : (
        <ul className="people__list">
          {entries.map((entry) => (
            <li key={entry.versionId} className="person issueRow">
              <span className="person__identity">
                <span className="person__detail">
                  {SECTION_NAMES[entry.kind]} · {dayAndTime(entry.createdAt)}
                </span>
                {entry.text === null || entry.text === '' ? null : (
                  <span className="issueRow__text">{entry.text}</span>
                )}
                {facts(entry).map((fact) => (
                  <span key={fact} className="person__detail">
                    {fact}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
